import { Controller } from "@hotwired/stimulus"

/**
 * Handles the Negotiation Toolkit form submission and result rendering.
 * POSTs to /negotiation-toolkit/generate, renders results or preview.
 */
export default class extends Controller {
  static targets = [
    "roleSlug", "currentSalary", "targetSalary", "negotiationType", "location",
    "submitButton", "submitText", "loading", "results", "resultsContent",
    "error", "errorText"
  ]
  static values = {
    generateUrl: String
  }

  roleSelected(event) {
    if (this.hasRoleSlugTarget) {
      this.roleSlugTarget.value = event.detail.slug || ""
    }
  }

  async generate(event) {
    event.preventDefault()

    const roleSlug = this.hasRoleSlugTarget ? this.roleSlugTarget.value : ""
    const currentSalary = this.hasCurrentSalaryTarget ? this.currentSalaryTarget.value : ""

    if (!roleSlug || !currentSalary) {
      this._showError("Please select a job role and enter your current salary.")
      return
    }

    this._setLoading(true)

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const body = {
        role_slug: roleSlug,
        current_salary: currentSalary,
        target_salary: this.hasTargetSalaryTarget ? this.targetSalaryTarget.value : "",
        negotiation_type: this.hasNegotiationTypeTarget ? this.negotiationTypeTarget.value : "pay_review",
        location: this.hasLocationTarget ? this.locationTarget.value : ""
      }

      const response = await fetch(this.generateUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify(body)
      })

      if (response.ok) {
        const data = await response.json()
        this._renderResults(data)
      } else if (response.status === 401) {
        window.location.href = "/users/sign_in"
      } else if (response.status === 403) {
        this._showError("You've reached your brief generation limit for this month. Upgrade for more.")
      } else {
        const data = await response.json().catch(() => ({}))
        this._showError(data.error || "Unable to generate brief. Please try again.")
      }
    } catch {
      this._showError("Network error — please try again.")
    } finally {
      this._setLoading(false)
    }
  }

  _setLoading(loading) {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.toggle("hidden", !loading)
    }
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = loading
    }
    if (loading) {
      if (this.hasResultsTarget) {
        this.resultsTarget.classList.add("hidden")
      }
      if (this.hasErrorTarget) {
        this.errorTarget.classList.add("hidden")
      }
    }
  }

  _showError(message) {
    if (this.hasErrorTarget && this.hasErrorTextTarget) {
      this.errorTextTarget.textContent = message
      this.errorTarget.classList.remove("hidden")
    }
  }

  _renderResults(data) {
    if (!this.hasResultsTarget || !this.hasResultsContentTarget) return

    const { preview, result } = data
    let html = ""

    // Market Position (always shown)
    if (result.market_position) {
      const mp = result.market_position
      const median = this._parseNZD(mp.median)
      html += `<div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 class="text-lg font-semibold text-gray-900 mb-3">Your Market Position</h2>
        ${mp.percentile ? `<p class="text-3xl font-bold text-cyan-600 mb-2">${mp.percentile}th percentile</p>` : ""}
        ${mp.position_summary ? `<p class="text-sm text-gray-600">${mp.position_summary}</p>` : ""}
        ${median ? `<p class="text-sm text-gray-500 mt-2">Median: $${median.toLocaleString()}</p>` : ""}
      </div>`
    }

    if (preview) {
      html += `<div class="bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-200 rounded-xl p-6 text-center">
        <p class="text-sm font-medium text-gray-700 mb-2">Upgrade to Premium for the full brief</p>
        <p class="text-xs text-gray-500 mb-3">Includes talking points, objection responses, email templates, and recommended salary range.</p>
        <a href="/dashboard/subscription" class="inline-block px-4 py-2 text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700 rounded-lg transition-colors">View Plans</a>
      </div>`
    } else {
      // Recommended Range
      if (result.recommended_range) {
        const rr = result.recommended_range
        const low = this._parseNZD(rr.low)
        const target = this._parseNZD(rr.target)
        const stretch = this._parseNZD(rr.stretch)
        if (low && target && stretch) {
          html += `<div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 class="text-lg font-semibold text-gray-900 mb-3">Recommended Salary Range</h2>
            <div class="grid grid-cols-3 gap-4 text-center">
              <div><p class="text-xs text-gray-500">Minimum</p><p class="text-lg font-bold text-gray-700">$${low.toLocaleString()}</p></div>
              <div class="border-x border-gray-200"><p class="text-xs text-gray-500">Target</p><p class="text-lg font-bold text-cyan-600">$${target.toLocaleString()}</p></div>
              <div><p class="text-xs text-gray-500">Stretch</p><p class="text-lg font-bold text-emerald-600">$${stretch.toLocaleString()}</p></div>
            </div>
          </div>`
        }
      }

      // Talking Points
      if (result.talking_points?.length) {
        html += `<div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 class="text-lg font-semibold text-gray-900 mb-3">Key Talking Points</h2>
          <div class="space-y-3">`
        result.talking_points.forEach((tp, i) => {
          html += `<div class="pl-4 border-l-2 border-cyan-300">
            <p class="font-medium text-gray-800">${i + 1}. ${tp.point}</p>
            ${tp.evidence ? `<p class="text-sm text-gray-500 mt-1">${tp.evidence}</p>` : ""}
          </div>`
        })
        html += `</div></div>`
      }

      // Objection Responses
      if (result.objection_responses?.length) {
        html += `<div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 class="text-lg font-semibold text-gray-900 mb-3">Handling Objections</h2>
          <div class="space-y-4">`
        result.objection_responses.forEach(or_item => {
          html += `<div>
            <p class="text-sm font-medium text-red-600">"${or_item.objection}"</p>
            <p class="text-sm text-gray-700 mt-1">${or_item.response}</p>
          </div>`
        })
        html += `</div></div>`
      }

      // Email Templates
      if (result.email_templates?.length) {
        html += `<div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 class="text-lg font-semibold text-gray-900 mb-3">Email Templates</h2>
          <div class="space-y-4">`
        result.email_templates.forEach(et => {
          html += `<div class="bg-gray-50 rounded-lg p-4">
            <p class="text-xs font-medium text-gray-500 mb-1">${et.purpose}</p>
            <p class="text-sm font-medium text-gray-800 mb-2">Subject: ${et.subject}</p>
            <pre class="text-sm text-gray-600 whitespace-pre-wrap font-sans">${et.body}</pre>
          </div>`
        })
        html += `</div></div>`
      }
    }

    // Saved brief link
    if (data.brief_id) {
      html += `<div class="text-center mt-4">
        <a href="/dashboard/salary-database/negotiation-briefs/${data.brief_id}" class="inline-flex items-center gap-1.5 text-sm font-medium text-cyan-600 hover:text-cyan-700">
          <i class="fas fa-bookmark text-xs"></i> This brief has been saved — view in dashboard
        </a>
      </div>`
    }

    // Disclaimer
    html += `<p class="text-xs text-gray-400 text-center mt-4">This brief is AI-generated using market data and general knowledge. Economic conditions, inflation figures, and market demand claims may not reflect the very latest data. This is general information only, not personalised financial advice.</p>`

    this.resultsContentTarget.innerHTML = html
    this.resultsTarget.classList.remove("hidden")
  }

  _parseNZD(value) {
    if (value == null) return null
    if (typeof value === "number") return isNaN(value) ? null : value
    const cleaned = String(value).replace(/[$,\s]/g, "").match(/-?\d+/)
    if (!cleaned) return null
    const num = parseInt(cleaned[0], 10)
    return isNaN(num) ? null : num
  }
}
